import { fromRouteHotelId, toRouteHotelId } from './hotel-id.util';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

export type MetaHotelTrackParams = {
  /** Hotel doc_id values used as Meta content_ids. */
  contentIds?: Array<string | number | null | undefined>;
  /** Optional hotel doc_id shorthand (same as contentIds). */
  docId?: string | number | null;
  value?: number | null;
  currency?: string | null;
  searchString?: string | null;
  checkinDate?: string | null;
  checkoutDate?: string | null;
  numAdults?: number | null;
  numChildren?: number | null;
  orderId?: string | null;
};

const PURCHASE_TRACKED_KEY_PREFIX = 'meta_pixel_purchase_';

/** Fires Meta Pixel PageView when fbq is available. */
export function trackMetaPageView(): void {
  safeTrack(() => fireMetaEvent('PageView'));
}

/** Hotel Search — destination or hotel-specific search. */
export function trackMetaSearch(params: MetaHotelTrackParams = {}): void {
  safeTrack(() => fireMetaEvent('Search', buildHotelPayload(params)));
}

/** Hotel detail view. */
export function trackMetaViewContent(params: MetaHotelTrackParams = {}): void {
  safeTrack(() => fireMetaEvent('ViewContent', buildHotelPayload(params)));
}

/** Checkout started for a hotel. */
export function trackMetaInitiateCheckout(params: MetaHotelTrackParams = {}): void {
  safeTrack(() => fireMetaEvent('InitiateCheckout', buildHotelPayload(params)));
}

/**
 * Successful booking. Dedupes by orderId in sessionStorage so refresh / multi-path
 * redirects do not double-fire Purchase.
 */
export function trackMetaPurchase(params: MetaHotelTrackParams = {}): void {
  safeTrack(() => {
    const orderId = params.orderId != null ? String(params.orderId).trim() : '';
    if (orderId) {
      const key = `${PURCHASE_TRACKED_KEY_PREFIX}${orderId}`;
      try {
        if (sessionStorage.getItem(key)) {
          return;
        }
        sessionStorage.setItem(key, '1');
      } catch {
        // sessionStorage unavailable — still fire once this call
      }
    }
    fireMetaEvent('Purchase', buildHotelPayload(params));
  });
}

/** Normalize a hotel doc_id for Meta content_ids (plain origin id, never base64). */
export function toMetaHotelContentId(value: string | number | null | undefined): string | null {
  if (value == null) {
    return null;
  }
  // Decode legacy encrypted route ids if a caller still passes one.
  const plain = toRouteHotelId(fromRouteHotelId(String(value)));
  return plain || null;
}

export function resolveMetaHotelContentIds(
  ...sources: Array<string | number | null | undefined>
): string[] {
  const ids = new Set<string>();
  for (const source of sources) {
    const id = toMetaHotelContentId(source);
    if (id) {
      ids.add(id);
    }
  }
  return Array.from(ids);
}

function buildHotelPayload(params: MetaHotelTrackParams): Record<string, unknown> {
  const contentIds = resolveMetaHotelContentIds(
    ...(params.contentIds || []),
    params.docId,
  );

  const payload: Record<string, unknown> = {
    content_type: 'hotel',
  };

  if (contentIds.length) {
    payload['content_ids'] = contentIds;
  }
  if (params.searchString?.trim()) {
    payload['search_string'] = params.searchString.trim();
  }
  if (params.checkinDate) {
    payload['checkin_date'] = params.checkinDate;
  }
  if (params.checkoutDate) {
    payload['checkout_date'] = params.checkoutDate;
  }
  if (typeof params.numAdults === 'number' && Number.isFinite(params.numAdults)) {
    payload['num_adults'] = params.numAdults;
  }
  if (typeof params.numChildren === 'number' && Number.isFinite(params.numChildren)) {
    payload['num_children'] = params.numChildren;
  }
  if (typeof params.value === 'number' && Number.isFinite(params.value)) {
    payload['value'] = params.value;
  }
  if (params.currency) {
    payload['currency'] = params.currency;
  }
  if (params.orderId != null && String(params.orderId).trim()) {
    payload['order_id'] = String(params.orderId).trim();
  }

  return payload;
}

function fireMetaEvent(eventName: string, payload?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') {
    return;
  }
  if (payload && Object.keys(payload).length) {
    window.fbq('track', eventName, payload);
  } else {
    window.fbq('track', eventName);
  }
}

/** Never let pixel/tracking failures interrupt UI flows (search, checkout, etc.). */
function safeTrack(run: () => void): void {
  try {
    run();
  } catch {
    // Pixel blocked, payload error, or storage unavailable — ignore
  }
}
